import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  knowledgeApi,
  type LeastSureItem,
  type LeastSurePattern,
  type LeastSurePatterns as PatternsData,
  type LeastSureSubject,
} from '../../services/api'
import { Avatar, Button, Card, EntryList, Eyebrow, Hint, Inline, Meta, Meter, Section, Stack, TileGrid } from '../../ds'
import { LeastSureRow, type LeastSureVerdicts, type VerdictOutcome } from './LeastSure'
import { beliefItemKey, confidenceRange, leastSureBeliefId, sharedEvidenceLine, typeLabel } from './leastSureFold'

/**
 * "Patterns she's seeing" — the least-sure window grouped by belief TYPE and identical
 * wording across subjects (`?group=type`, MAR-389; the canvas's be0/be1).
 *
 * ── What a pattern claims ───────────────────────────────────────────────────────────────
 *
 * The server groups ≥ 2 distinct customers (or products) holding an ACTIVE belief of one type
 * whose wording is the SAME sentence — case, whitespace and a trailing full stop aside. ⚠️ It
 * never matches "similar" sentences, so a pattern here is never words she did not write. Each
 * member was re-read through its own record before it was listed, so a belief already settled
 * elsewhere is not a member.
 *
 * ── "Right for all N" is N verdicts ─────────────────────────────────────────────────────
 *
 * There is no batch verdict route. The button fires the SAME per-belief verdict the rows use
 * (`useLeastSureVerdicts`, each member with its own `expectedUpdatedAt`), at most
 * `CONFIRM_ALL_CONCURRENCY` at a time, and then says how many landed. ⚠️ A partial result is
 * said as one: "Recorded for 3 of 4", and the group opens so the one that did not land shows its
 * own reason on its own row. Members already settled (here or in the least-sure list — the
 * verdict state is shared) or with a verdict already in flight are not sent again.
 *
 * ⚠️ Everything is counted in SUBJECTS, as the badge is: one customer holding the sentence twice
 * is one customer, so the button, the badge and the result never disagree. A subject is
 * recorded when every one of its beliefs was; a verdict that was not sent because one was
 * already in flight (`skipped`) is said apart — it has no refusal of its own to show.
 *
 * ── The card (MAR-401, the canvas's Knowledge-1) ─────────────────────────────────────────
 *
 * Each pattern is a card in a two-across grid: "<Type> · N customers", the members' faces, the
 * sentence in her voice, the bar and its range, then "One by one" and "Right for all N". The
 * faces stand in for the names line — each face is named for a screen reader, and "One by one"
 * opens the members' own rows, names and all. ⚠️ A "From …" line is printed only when EVERY
 * member's first evidence item reads the same (`sharedEvidenceLine`); otherwise one member's
 * evidence would be claimed for the rest. Members' beliefs are left out of the one-by-one list
 * below (`patternMemberKeys`), so a belief never shows twice.
 *
 * ── States ──────────────────────────────────────────────────────────────────────────────
 *
 * Quiet while loading and when there is no pattern — patterns are an addition to the list
 * below, not the list. A FAILED read says so, with a retry: "no patterns" is never how a
 * failure reads. A read that could not check every belief says that too.
 */

/** How many patterns one read asks for. */
export const PATTERNS_LIMIT = 5

/** Verdict POSTs "Right for all N" keeps in flight at once — a pattern can hold 60 members. */
export const CONFIRM_ALL_CONCURRENCY = 6

/** Run `work` over `items`, never more than `limit` at once; results in input order. */
export async function mapBounded<T, R>(items: T[], limit: number, work: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length)
  let next = 0
  const lane = async() => {
    while (next < items.length) {
      const i = next++
      out[i] = await work(items[i])
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane))
  return out
}

/** Kept here for the callers that import it from the patterns; it lives in `leastSureFold`. */
export { typeLabel }

interface Snapshot {
  key: string
  state: 'loading' | 'failed' | 'loaded'
  data: PatternsData | null
}

export interface PatternsHandle {
  state: 'idle' | 'loading' | 'failed' | 'loaded'
  data: PatternsData | null
  reload: () => void
}

/**
 * The pattern read for one (workspace, scope) — requested once while `enabled` and kept, like
 * `useLeastSure`. Each snapshot is stamped with its key and matched at the read, so a slow read
 * for another workspace or scope never lands as this one.
 */
export function useLeastSurePatterns(
  workspaceId: string | undefined,
  scope: LeastSureSubject,
  enabled = true
): PatternsHandle {
  const [snaps, setSnaps] = useState<Record<string, Snapshot>>({})
  const request = useRef<Record<string, number>>({})
  const requested = useRef<Set<string>>(new Set())
  const key = workspaceId ? JSON.stringify([workspaceId, scope]) : null

  const load = useCallback(async() => {
    if (!workspaceId || !key) return
    const mine = (request.current[key] = (request.current[key] ?? 0) + 1)
    const write = (s: Snapshot) => {
      if (mine === request.current[key]) setSnaps((all) => ({ ...all, [key]: s }))
    }
    write({ key, state: 'loading', data: null })
    try {
      const res = await knowledgeApi.getLeastSurePatterns(workspaceId, scope, PATTERNS_LIMIT)
      // ⚠️ Fail closed on a server that did not APPLY `group=type` (one that predates it
      // answers the plain list): its `items` are not patterns, and reading them as "none"
      // would be the quiet failure. The same for the scope echo.
      if (
        !res.success ||
        !res.data ||
        !Array.isArray(res.data.groups) ||
        !res.data.bound ||
        res.data.bound.group !== 'type' ||
        res.data.bound.scope !== scope
      ) {
        throw new Error('patterns_unavailable')
      }
      write({ key, state: 'loaded', data: res.data })
    } catch {
      write({ key, state: 'failed', data: null })
    }
  }, [workspaceId, scope, key])

  useEffect(() => {
    if (!key || !enabled || requested.current.has(key)) return
    requested.current.add(key)
    void load()
  }, [load, key, enabled])

  const current = key ? snaps[key] : undefined
  return {
    state: !key || (!current && !enabled) ? 'idle' : (current?.state ?? 'loading'),
    data: current?.data ?? null,
    reload: () => {
      void load()
    },
  }
}

/** How many faces a card draws; the eyebrow's count says how many there are in all. */
export const PATTERN_FACES = 5

/** One face per distinct subject, in the server's order, at most `PATTERN_FACES`. */
function faces(members: LeastSureItem[]): LeastSureItem[] {
  const seen = new Set<string>()
  const out: LeastSureItem[] = []
  for (const m of members) {
    if (seen.has(m.id)) continue
    seen.add(m.id)
    out.push(m)
    if (out.length === PATTERN_FACES) break
  }
  return out
}

/**
 * The words a face takes its initials from — a person's name or a product's title, and NOTHING
 * when there is none (a blank face), never the "Name unavailable…" sentence's initials.
 */
function faceName(m: LeastSureItem): string {
  if (!m.label) return ''
  if (m.scope === 'customer') {
    const l = m.label as { firstName: string | null; lastName: string | null }
    return `${l.firstName || ''} ${l.lastName || ''}`.trim()
  }
  return (m.label as { title: string | null }).title ?? ''
}

/**
 * The faces' ONE accessible name (PR #359 review): the named subjects among the faces, how many
 * more there are, and how many have no name to say — once, as a count, never one "Name
 * unavailable…" announcement per face. The faces themselves are hidden from assistive tech.
 */
export function facesLabel(members: LeastSureItem[]): string {
  const seen = new Set<string>()
  const distinct: LeastSureItem[] = []
  for (const m of members) {
    if (seen.has(m.id)) continue
    seen.add(m.id)
    distinct.push(m)
  }
  const shown = distinct.slice(0, PATTERN_FACES)
  const names = shown.map(faceName).filter(Boolean)
  const more = distinct.length - shown.length
  const unnamed = distinct.filter((m) => !faceName(m)).length
  const product = distinct[0]?.scope === 'product'
  const parts: string[] = []
  if (names.length) parts.push(`${names.join(', ')}${more > 0 ? ` and ${more} more` : ''}`)
  else if (more > 0) parts.push(`${more} more`)
  if (unnamed > 0) {
    parts.push(
      product
        ? `${unnamed} of ${distinct.length} without a title`
        : `${unnamed} of ${distinct.length} ${distinct.length === 1 ? 'name' : 'names'} withheld`
    )
  }
  return parts.join('; ')
}

/**
 * Every belief the loaded patterns list, as `beliefItemKey`s — what the one-by-one list leaves
 * out. Empty until the read lands (and on a failed read): then nothing is left out.
 */
export function patternMemberKeys(data: PatternsData | null | undefined): ReadonlySet<string> {
  const keys = new Set<string>()
  for (const g of data?.groups ?? []) {
    for (const m of g.members) {
      const k = beliefItemKey(m)
      if (k) keys.add(k)
    }
  }
  return keys
}

/**
 * The "Right for all N" result, said as it happened, in subjects. `skipped` subjects had a
 * verdict already in flight, so nothing was sent for them and they have no refusal to show —
 * they are said apart from the refused ones, never folded into "each says why below".
 */
export function batchNote(
  sent: number,
  recorded: number,
  skipped = 0
): { tone: 'ok' | 'bad'; text: string } {
  if (sent === 0) return { tone: 'ok', text: 'Nothing left to confirm — each one is already settled.' }
  const refused = sent - recorded - skipped
  const inFlight = skipped
    ? ` ${skipped === 1 ? 'One was' : `${skipped} were`} already being recorded, so not sent again.`
    : ''
  if (refused === 0) {
    if (recorded === sent) return { tone: 'ok', text: sent === 1 ? 'Recorded.' : `Recorded for all ${sent}.` }
    return { tone: 'ok', text: `Recorded for ${recorded} of ${sent}.${inFlight}` }
  }
  return {
    tone: 'bad',
    text: `Recorded for ${recorded} of ${sent}. ${refused === 1 ? 'One was' : `${refused} were`} not — each says why below.${inFlight}`,
  }
}

function PatternGroup({
  group,
  open,
  onToggle,
  verdicts,
  onOpen,
}: {
  group: LeastSurePattern
  open: boolean
  onToggle: () => void
  verdicts: LeastSureVerdicts
  onOpen: (item: LeastSureItem) => void
}) {
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null)
  const noun = group.scope === 'customer' ? 'customers' : 'products'
  const pct = Math.round(Math.max(0, Math.min(1, group.confidence.min)) * 100)
  const settle = verdicts.settle
  // Only the members still open to a verdict: one settled here or in the list, or with a verdict
  // already in flight, is not resent.
  const pending = group.members.filter((m) => {
    const row = verdicts.rowFor(m)
    return (
      !row.stale &&
      !row.busy &&
      (row.belief.status === 'active' || row.belief.status == null) &&
      !!leastSureBeliefId(m.scope, m.reason.belief)
    )
  })
  // Counted in subjects, like the badge: one customer saying it twice is one customer.
  const pendingSubjects = new Set(pending.map((m) => m.id)).size

  const confirmAll = async() => {
    if (!settle || busy) return
    setBusy(true)
    setNote(null)
    const sent = [...pending]
    const outcomes: VerdictOutcome[] = await mapBounded(sent, CONFIRM_ALL_CONCURRENCY, (m) => settle(m, 'confirmed'))
    // Per subject: refused if any of its verdicts was, else skipped if any was, else recorded.
    const bySubject = new Map<string, VerdictOutcome>()
    sent.forEach((m, i) => {
      const prev = bySubject.get(m.id)
      const o = outcomes[i]
      if (!prev || o === 'refused' || (o === 'skipped' && prev === 'recorded')) bySubject.set(m.id, o)
    })
    const per = [...bySubject.values()]
    const result = batchNote(
      per.length,
      per.filter((o) => o === 'recorded').length,
      per.filter((o) => o === 'skipped').length
    )
    setNote(result)
    setBusy(false)
    // A partial result opens the group, so each member that did not land shows its own reason.
    if (result.tone === 'bad' && !open) onToggle()
  }

  const from = sharedEvidenceLine(group.members)
  const membersId = `kn-pattern-members-${useId().replace(/:/g, '')}`
  return (
    <Card data-testid="kn-pattern" data-scope={group.scope} data-count={group.subjects}>
      <Stack gap="sm">
        <Inline justify="between">
          <Eyebrow tone="muted" data-testid="kn-pattern-type">
            {typeLabel(group.type)} · {group.subjects} {noun}
          </Eyebrow>
          {/* One name for the faces, said once — each face is decoration beside it. */}
          <Inline wrap={false} data-testid="kn-pattern-faces" role="img" aria-label={facesLabel(group.members)}>
            {faces(group.members).map((m) => (
              <Avatar key={m.id} size="xs" name={faceName(m)} label={false} />
            ))}
          </Inline>
        </Inline>
        <p className="t-voice" data-testid="kn-pattern-statement">
          {group.statement || 'A belief she holds with no wording recorded.'}
        </p>
        {from && <Hint data-testid="kn-pattern-from">From {from}</Hint>}
        <Meter
          layout="inline"
          size="sm"
          label={<span className="mv-sr">How sure</span>}
          value={pct}
          valueText={confidenceRange(group.members)}
        />
        <Inline>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={open}
            aria-controls={membersId}
            data-testid="kn-pattern-toggle"
            onClick={onToggle}
          >
            {open ? 'Fold them' : 'One by one'}
          </Button>
          {settle && pendingSubjects > 0 && (
            <Button
              variant="sage"
              size="sm"
              data-testid="kn-pattern-confirm-all"
              disabled={busy}
              onClick={() => void confirmAll()}
            >
              {busy
                ? 'Recording…'
                : pendingSubjects === 1
                  ? 'Right for this one'
                  : pendingSubjects === group.subjects
                    ? `Right for all ${pendingSubjects}`
                    : `Right for the ${pendingSubjects} left`}
            </Button>
          )}
        </Inline>
        {note && (
          <Meta role="status" data-testid="kn-pattern-note" data-tone={note.tone}>
            {note.text}
          </Meta>
        )}
        <div id={membersId} hidden={!open}>
          {open && (
            <EntryList label={`${typeLabel(group.type)}: each ${noun === 'customers' ? 'customer' : 'product'}`}>
            {group.members.map((m) => (
              <LeastSureRow
                key={`${m.scope}:${m.id}:${leastSureBeliefId(m.scope, m.reason.belief) ?? ''}`}
                item={m}
                showTag={false}
                verdicts={verdicts}
                onOpen={onOpen}
              />
            ))}
            </EntryList>
          )}
        </div>
      </Stack>
    </Card>
  )
}

export default function LeastSurePatterns({
  read,
  onOpen,
  workspaceId,
  verdicts,
}: {
  read: PatternsHandle
  onOpen: (item: LeastSureItem) => void
  workspaceId?: string
  verdicts: LeastSureVerdicts
}) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set())
  if (read.state === 'idle' || read.state === 'loading') return null
  if (read.state === 'failed' || !read.data) {
    return (
      <Hint data-testid="kn-patterns-failed">
        She couldn’t look for patterns just now — the list below is unaffected.{' '}
        <Button variant="link" onClick={read.reload}>
          Try again
        </Button>
      </Hint>
    )
  }
  const { groups, bound } = read.data
  const exhaustive = bound.exhaustive
  if (groups.length === 0) {
    return exhaustive ? null : (
      <Hint data-testid="kn-patterns-partial">She could not check every belief for patterns this time.</Hint>
    )
  }
  const openKey = (k: string) => JSON.stringify([workspaceId ?? null, k])
  return (
    <Section
      title={bound.scope === 'product' ? 'Patterns across your catalogue' : 'Patterns she’s seeing'}
      description="The same belief, word for word, about more than one. Settle them together or one by one."
    >
      <TileGrid columns={2} data-testid="kn-patterns">
        {groups.map((g) => {
          const k = openKey(g.key)
          return (
            <PatternGroup
              key={g.key}
              group={g}
              open={opened.has(k)}
              onToggle={() =>
                setOpened((prev) => {
                  const next = new Set(prev)
                  if (next.has(k)) next.delete(k)
                  else next.add(k)
                  return next
                })
              }
              verdicts={verdicts}
              onOpen={onOpen}
            />
          )
        })}
      </TileGrid>
      {(bound.groupsUnchecked ?? 0) > 0 ? (
        <Hint data-testid="kn-patterns-more">
          Showing the {groups.length} largest patterns she found. She has seen signs of more — none larger than these — that she has not checked yet.
        </Hint>
      ) : (
        bound.groupsFound > groups.length && (
          <Hint data-testid="kn-patterns-more">
            Showing {groups.length} of the {bound.groupsFound} patterns she found, largest first.
          </Hint>
        )
      )}
      {!exhaustive && (
        <Hint data-testid="kn-patterns-partial">
          She could not check every belief for patterns this time, so a pattern may be missing or larger than shown.
        </Hint>
      )}
    </Section>
  )
}
